import Page from '@/gf/components/Page'
import useMsgs from '@/gf/hooks/useMsgs'
import {
  completesCreateQuoteFromScratch,
  initiatesCreateQuoteFromScratch,
} from '@/gf/modules/GrammarEvents'
import { cleanGraphQlInput } from '@/gf/modules/GraphQL'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DeliveryMethod, useStoreOrderRequestLazyQuery } from '../_gen/gql'
import Frame from '../components/Frame'
import useSession from '../hooks/useSession'
import Urls from '../modules/Urls'
import QuoteBuilder from './Request/QuoteBuilder'
import { OnErrorCallback } from './Request/QuoteBuilder/Errors'
import { deletePersistedForm, DIRECT_QUOTE_KEY } from './Request/QuoteBuilder/useForm'
import { FormItem } from './Request/types'
import DocumentUploadProvider from './Request/QuoteUpload/DocumentUploadProvider'
import QuoteAssistantModal from './Request/QuoteUpload/QuoteAssistantModal'
import QuoteUploadModal from './Request/QuoteUpload/QuoteUploadModal'
import useQuoteUpload from './Request/QuoteUpload/useQuoteUpload'
import QuoteUploadBar from './Request/QuoteUpload/QuoteUploadBar'

const CreateQuote = () => {
  const navigate = useNavigate()
  const { store } = useSession()
  const [_, msgs] = useMsgs()
  const [getStoreOrderRequest] = useStoreOrderRequestLazyQuery()
  const quoteUpload = useQuoteUpload()

  // Track current form items for AI Assistant modal to show current state
  const [currentFormItems, setCurrentFormItems] = useState<FormItem[]>([])

  // Check if we have line items from context (from initial extraction or AI corrections)
  const hasContextLineItems = quoteUpload.lineItems.length > 0

  // Clear localStorage when AI corrections are applied so form reinitializes with new values
  if (hasContextLineItems) {
    deletePersistedForm(DIRECT_QUOTE_KEY)
  }

  const onQuoteSuccess = async (storeOrderId: string) => {
    const { data: storeOrderRequestData } = await getStoreOrderRequest({
      variables: { storeOrderId },
    })

    completesCreateQuoteFromScratch({ storeOrderId })

    const to = storeOrderRequestData?.storeOrder?.request?.id
      ? `${Urls.requestForQuote(storeOrderRequestData.storeOrder.request.id)}?quoteSubmitted=1`
      : `/orders/${storeOrderId}`

    navigate(to)
  }

  const onError: OnErrorCallback = (message) => msgs.add(message, 'negative')

  const canCreateQuote = store.subscription.isPro

  // Default empty item for when there are no extracted line items
  const defaultItems = [
    {
      id: null,
      partNumber: '',
      altPartNumbers: [] as string[],
      description: '',
      unitPrice: null,
      quantity: 1,
      inStock: true,
      externalId: null,
      taskNumber: null,
      suggestion: null,
      availabilityDate: null,
      rfqPartId: null,
      rfqPartNumber: null,
    },
  ]

  // Use context line items (from extraction or AI corrections) if available
  const items = hasContextLineItems ? quoteUpload.lineItems : defaultItems

  const initialValues = {
    customerContactId: null,
    // Prefer extracted value when present. `null` from the extractor means
    // "not on the doc" — keep the form default rather than zeroing out.
    shippingCost:
      hasContextLineItems && quoteUpload.shippingCost !== null ? quoteUpload.shippingCost : null,
    taxCost: hasContextLineItems && quoteUpload.taxCost !== null ? quoteUpload.taxCost : null,
    customerDiscount: null,
    quoteNumber: null,
    deliveryMethod: DeliveryMethod.Pickup,
    items,
    pickupAddress: store.address ? cleanGraphQlInput(store.address) : null,
    shippingAddress: null,
    neededByDate: null,
    urgency: null,
    comments: '',
  }

  return (
    <>
      <QuoteUploadModal
        uploadStage={quoteUpload.uploadStage}
        error={quoteUpload.error}
        onClose={quoteUpload.onDismiss}
      />

      <QuoteAssistantModal
        open={quoteUpload.assistantModalOpen}
        onClose={quoteUpload.onCloseAssistantModal}
        attachmentUrls={quoteUpload.attachmentUrls}
        currentLineItems={currentFormItems}
      />

      <Frame>
        <Page className="max-w-[66.5rem] mx-auto w-full pt-8 space-y-8">
          <h2 className="text-xl font-medium leading-7 max-w-lg ">Create Quote</h2>

          <QuoteBuilder
            key={hasContextLineItems ? `document-v${quoteUpload.lineItemsVersion}` : 'initial'}
            initialValues={initialValues}
            images={[]}
            onSuccess={onQuoteSuccess}
            actions={
              <QuoteUploadBar
                onError={quoteUpload.onError}
                onUploadComplete={quoteUpload.onUploadComplete}
                onUploadStarted={quoteUpload.onUploadStarted}
                rfqId={quoteUpload.rfqId}
              />
            }
            onError={onError}
            onCancel={() => navigate('/rfqs')}
            disabled={!canCreateQuote}
            customerFeeRate={null}
            onTouched={initiatesCreateQuoteFromScratch}
            showWhenCustomerDiscountAutoFilled
            onItemsChange={setCurrentFormItems}
          />
        </Page>
      </Frame>
    </>
  )
}

const CreateQuoteWithDocumentUploadProvider = () => (
  <DocumentUploadProvider rfqId={null}>
    <CreateQuote />
  </DocumentUploadProvider>
)

export default CreateQuoteWithDocumentUploadProvider
